import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import cameraAngleFileApi from '@services/cameraAngleFileApi';
import resourceApi from '@services/resourceApi';
import AlertModal from '@components/common/AlertModal';

const CameraAngleFileUploadModal = ({ show, angleId, onClose, onSuccess, isNewMode = false }) => {
  const [uploadingFile, setUploadingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const fileInputRef = useRef(null);
  
  // 고유한 ID 생성
  const fileInputId = `angle-file-input-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // 컴포넌트 표시 상태 변경 시 파일 입력 초기화
  useEffect(() => {
    if (show && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [show]);

  // 파일 선택 버튼 클릭 핸들러
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  // 파일 선택 핸들러
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    
    // 여러 파일을 순차적으로 처리
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadingFile(file);

      try {
        // 1. ResourceApi를 사용한 파일 업로드
        const resource = await resourceApi.uploadFile(file, `앵글파일: ${file.name}`);

        if (isNewMode) {
          // 신규 모드: 임시 파일 정보만 생성
          const tempFileData = {
            fileName: file.name,
            originalFileName: file.name,
            fileResourceId: resource.id,
            isActive: true,
          };

          if (onSuccess) {
            onSuccess(tempFileData);
          }
        } else {
          // 수정 모드: 실제 API 등록
          const fileData = {
            fileName: file.name,
            originalFileName: file.name,
            fileResourceId: resource.id,
            isActive: true,
          };
          
          const addResponse = await cameraAngleFileApi.addFile(angleId, fileData);

          if (addResponse.success) {
            if (onSuccess) {
              onSuccess(addResponse.data);
            }
          } else {
            setAlertMessage(`${file.name}: ${addResponse.message || '파일 등록에 실패했습니다.'}`);
            setShowAlertModal(true);
          }
        }
      } catch (error) {
        console.error(`${file.name} 파일 업로드 실패:`, error);
        setAlertMessage(`${file.name}: 파일 업로드에 실패했습니다.`);
        setShowAlertModal(true);
      }
    }
    
    setIsUploading(false);
    if (!showAlertModal) {
      handleClose();
    }
  };


  const handleClose = () => {
    if (!isUploading) {
      setUploadingFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <motion.div
        key={`file-upload-modal-${fileInputId}`}
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="modal-content"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>앵글 파일 추가</h2>
            <button className="close-button" onClick={handleClose} disabled={isUploading}>
              ×
            </button>
          </div>

          <div className="modal-body">
            {isUploading ? (
              <div className="uploading-state">
                <div className="loading-spinner"></div>
                <p>파일을 업로드하고 있습니다...</p>
                {uploadingFile && (
                  <p className="file-name">현재 업로드 중: {uploadingFile.name}</p>
                )}
              </div>
            ) : (
              <div className="upload-area">
                {/* 숨겨진 파일 입력 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id={fileInputId}
                />
                
                <div className="upload-content">
                  <div className="upload-icon">📁</div>
                  <p className="main-text">앵글 파일을 선택하세요</p>
                  <p className="sub-text">
                    지원 형식: 모든 파일 (확장자 없는 앵글 파일 포함)
                  </p>
                  <p className="click-hint">다중 선택 가능 - 여러 파일을 한 번에 선택할 수 있습니다</p>
                  
                  {/* 방법 1: 버튼 클릭 */}
                  <button
                    type="button"
                    className="btn-select-file"
                    onClick={handleFileButtonClick}
                  >
                    파일 선택 (버튼)
                  </button>
                  
                  {/* 방법 2: 라벨 클릭 */}
                  <label 
                    htmlFor={fileInputId} 
                    className="btn-select-file"
                    style={{ marginTop: '10px', backgroundColor: '#2196F3' }}
                  >
                    파일 선택 (라벨)
                  </label>
                  
                  {/* 방법 3: 직접 보이는 파일 입력 */}
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="file-input-visible"
                    style={{ marginTop: '10px' }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn-cancel"
              onClick={handleClose}
              disabled={isUploading}
            >
              취소
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* 알림 모달 */}
      {showAlertModal && (
        <AlertModal
          show={showAlertModal}
          message={alertMessage}
          onConfirm={() => setShowAlertModal(false)}
        />
      )}

      <style jsx="true">{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          color: #666;
        }

        .close-button:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .modal-body {
          padding: 30px 20px;
        }

        .upload-area {
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          background: #f9f9f9;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .upload-area:hover {
          border-color: #4caf50;
          background: #f0f8f0;
        }

        .upload-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .upload-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .main-text {
          font-size: 16px;
          color: #333;
          margin-bottom: 8px;
        }

        .sub-text {
          font-size: 14px;
          color: #666;
        }

        .click-hint {
          font-size: 13px;
          color: #888;
          font-style: italic;
        }

        .btn-select-file {
          background: #4caf50;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
          display: inline-block;
          text-decoration: none;
          text-align: center;
        }

        .btn-select-file:hover {
          background: #45a049;
        }

        .file-input-visible {
          margin-top: 15px;
          padding: 12px;
          border: 2px solid #4caf50;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 14px;
          width: 100%;
          max-width: 300px;
        }

        .file-input-visible:hover {
          border-color: #45a049;
          background: #f0f8f0;
        }

        .file-input-visible:focus {
          outline: none;
          border-color: #4caf50;
          box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
        }

        .uploading-state {
          text-align: center;
          padding: 20px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4caf50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .uploading-state p {
          margin-top: 20px;
          color: #666;
        }

        .file-name {
          font-weight: 500;
          color: #333;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          padding: 15px 20px;
          border-top: 1px solid #e0e0e0;
          gap: 10px;
        }

        .btn-cancel {
          padding: 8px 20px;
          border: 1px solid #ddd;
          background: white;
          color: #666;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f5f5f5;
        }

        .btn-cancel:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      `}</style>
    </>
  );
};

export default CameraAngleFileUploadModal;